import { FC, useRef, useState, useContext, useEffect } from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { ReactComponent as ThemeIcon } from '../../assets/icons/theme_icon_02.svg';

import * as S from './styles';
import title from '../../assets/title.svg';
import logo from '../../assets/header-logo.svg';
import { ReactComponent as PdfGenerationIcon } from '../../assets/icons/pdf-generation.svg';
import loadingSpinnerImage from '../../assets/icons/loading-spinner.gif';
import { Menu } from './Menu';
import { useOutsideClick } from '../../hooks/useOutsideElement';
import { ThemeContext } from 'styled-components';
import { ThemeEnum } from '../../handlers/ThemeHandler';
import {
  LanguagesEnum,
  TranslationContext
} from '../../handlers/TranslationsHandler';
import { exportConfig, importConfig } from '../../helpers/exportImportConfig';
import { TilesContext } from '../../handlers/TilesHandler';
import { JsonUploader } from '../JsonUploader';
import { useTour } from '@reactour/tour';
import { TutorialContext, TutorialEnum } from '../../handlers/TutorialHandler';
import { useMatomo } from '@datapunt/matomo-tracker-react';
import { MatomoAction, MatomoCategory } from '../../Constants';
import { SiteContext } from '../../handlers/SiteHandler';

export const Header: FC = () => {
  const [showMainMenu, setShowMainMenu] = useState(false);
  const [showThemeMenu, setShowThemeMenu] = useState(false);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);

  const MainMenuRef = useRef(null);
  const MainMenuButtonRef = useRef(null);
  const ThemeMenuRef = useRef(null);
  const ThemeMenuButtonRef = useRef(null);
  const LanguageMenuRef = useRef(null);
  const LanguageMenuButtonRef = useRef(null);

  const themeContext = useContext(ThemeContext) as any;
  const { setGenerateCatalogue, loading } = useContext(SiteContext);
  const tilesContext = useContext(TilesContext);
  const { language, setLanguage, translate } = useContext(TranslationContext);
  const { tutorialType, setCloseModal } = useContext(TutorialContext);

  const { currentStep } = useTour();

  const inputFileRef = useRef(null);

  const { trackEvent } = useMatomo();

  useOutsideClick([MainMenuRef, MainMenuButtonRef], () => {
    if (
      (tutorialType === TutorialEnum.three && currentStep === 2) ||
      (tutorialType === TutorialEnum.three && currentStep === 3)
    ) {
      return;
    }
    setShowMainMenu(false);
  });

  useOutsideClick([ThemeMenuRef, ThemeMenuButtonRef], () =>
    setShowThemeMenu(false)
  );

  useOutsideClick([LanguageMenuRef, LanguageMenuButtonRef], () => {
    setShowLanguageMenu(false);
  });

  const setJsonImport = (json: any) => {
    importConfig(
      json,
      themeContext.setTheme,
      themeContext.setLogo,
      tilesContext.setMainTiles,
      tilesContext.isInSpecialMode
    );
  };

  useEffect(() => {
    if (tutorialType === TutorialEnum.two && currentStep === 3) {
      setShowThemeMenu(true);
    }
    if (
      (tutorialType === TutorialEnum.three && currentStep === 2) ||
      (tutorialType === TutorialEnum.three && currentStep === 3) ||
      (tutorialType === TutorialEnum.three && currentStep === 4)
    ) {
      setShowMainMenu(true);
    }
  }, [currentStep]);

  return (
    <S.Header>
      <S.Logo>
        <img src={title} />
      </S.Logo>
      <div>
        <S.RightLogo src={logo}></S.RightLogo>
        <S.MenuButton
          style={{ width: 45 }}
          title={translate('generateCatalogue')}
          onClick={() => {
            setGenerateCatalogue(true);
          }}
        >
          {loading ? (
            <img src={loadingSpinnerImage} style={{ width: '26px' }} />
          ) : (
            <PdfGenerationIcon style={{ width: '26px' }} />
          )}
        </S.MenuButton>
        <S.MenuButton
          data-tour="menu"
          title={translate('changeTheme')}
          onClick={() => setShowThemeMenu(!showThemeMenu)}
          ref={ThemeMenuButtonRef}
        >
          <ThemeIcon style={{ width: '20px' }} />
          <Menu shouldShow={showThemeMenu} innerRef={ThemeMenuRef}>
            {Object.values(ThemeEnum)
              .filter(t => isNaN(t as number))
              .map((tv, i) => (
                <li
                  data-tour={`header-${i}`}
                  key={`theme${i}`}
                  onClick={() => {
                    themeContext.setTheme(ThemeEnum[tv]);
                    trackEvent({
                      category: MatomoCategory.Theme,
                      action: MatomoAction.ThemeChanged,
                      name: tv as string
                    });
                  }}
                  className={
                    themeContext.theme === ThemeEnum[tv] ? 'selected' : ''
                  }
                >
                  <span>{translate(tv as string)}</span>
                </li>
              ))}
          </Menu>
        </S.MenuButton>
        <S.MenuButton
          data-tour="menu"
          title={translate('changeLanguage')}
          onClick={() => setShowLanguageMenu(!showLanguageMenu)}
          ref={LanguageMenuButtonRef}
        >
          {language}
          <Menu shouldShow={showLanguageMenu} innerRef={LanguageMenuRef}>
            {Object.values(LanguagesEnum).map(lang => (
              <li
                key={`lang-option${lang}`}
                onClick={() => {
                  localStorage.setItem('language', lang);
                  setLanguage(lang);
                }}
                className={lang === language ? 'selected' : ''}
              >
                <span>{lang}</span>
              </li>
            ))}
          </Menu>
        </S.MenuButton>
        <S.MenuButton
          ref={MainMenuButtonRef}
          onClick={() => {
            setShowMainMenu(!showMainMenu);
          }}
          data-tour={'menu-1'}
        >
          <MoreVertIcon />
          <Menu shouldShow={showMainMenu} innerRef={MainMenuRef}>
            <li
              onClick={() => {
                setShowMainMenu(false);
              }}
            >
              <a
                href="https://www.ui.city/impressum"
                target="_blank"
                rel="noreferrer"
              >
                {translate('imprint')}
              </a>
            </li>
            <li
              onClick={() => {
                setShowMainMenu(false);
              }}
            >
              <a
                href="https://www.ui.city/datenschutz"
                target="_blank"
                rel="noreferrer"
              >
                {translate('dataProtection')}
              </a>
            </li>
            <li
              data-tour={'menu-export'}
              onClick={() => {
                trackEvent({
                  category: MatomoCategory.Dashboard,
                  action: MatomoAction.Export
                });
                exportConfig();
              }}
            >
              <span>{translate('exportConfig')}</span>
            </li>
            <li
              data-tour={'menu-import'}
              onClick={() => {
                const inputEl = inputFileRef?.current;
                trackEvent({
                  category: MatomoCategory.Dashboard,
                  action: MatomoAction.Import
                });

                if (inputEl) {
                  (inputEl as any)?.click();
                }
              }}
            >
              <span>{translate('importConfig')}</span>
            </li>
            <li
              onClick={() => {
                localStorage.setItem('tutorialMode', JSON.stringify(true));
                setCloseModal(false);
                setShowMainMenu(false);
              }}
            >
              <span>{translate('startTutorialAgain')}</span>
            </li>
            <li
              data-tour={'menu-reset'}
              onClick={() => {
                trackEvent({
                  category: MatomoCategory.Dashboard,
                  action: MatomoAction.Reset
                });
                themeContext.resetTheme();
                tilesContext.resetTiles();
                setShowMainMenu(false);
              }}
            >
              <span>{translate('resetToDefault')}</span>
            </li>

            <JsonUploader setJson={setJsonImport} inputFileRef={inputFileRef} />
          </Menu>
        </S.MenuButton>
      </div>
    </S.Header>
  );
};
